import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { AlertTriangle, ExternalLink } from 'lucide-react';

export default function EmployeesAtRisk({ month, onViewEmployee }) {
  const [atRisk, setAtRisk] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (month) {
      loadAtRisk();
    }
  }, [month]);
  
  const loadAtRisk = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/employees-at-risk?month=${month}`);
      setAtRisk(response.data.employees_at_risk || []);
    } catch (err) {
      console.error('Error loading at-risk employees:', err);
      setError(err.response?.data?.detail || 'Eroare la încărcarea listei');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <Card className="border-red-200">
        <CardContent className="py-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    return null; // Silently fail for this component
  }
  
  if (atRisk.length === 0) {
    return null; // Don't show if no one is at risk
  }
  
  const riskLevelColors = {
    "Ridicat": "bg-red-100 text-red-800",
    "Mediu": "bg-orange-100 text-orange-800",
    "Scăzut": "bg-yellow-100 text-yellow-800"
  };
  
  return (
    <Card className="border-red-200 bg-red-50/30" data-testid="employees-at-risk-section">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-5 h-5" />
          Angajați în Risc
        </CardTitle>
        <CardDescription>
          {atRisk.length} angajați necesită atenție în această lună
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="py-2 px-3 text-left font-medium">Nume</th>
                <th className="py-2 px-3 text-left font-medium">Motiv risc</th>
                <th className="py-2 px-3 text-center font-medium">Scor</th>
                <th className="py-2 px-3 text-center font-medium">Nivel</th>
                <th className="py-2 px-3 text-right font-medium">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {atRisk.map((emp) => (
                <tr key={emp.employee_id} className="border-b hover:bg-red-50">
                  <td className="py-2 px-3 font-medium">{emp.employee_name}</td>
                  <td className="py-2 px-3">
                    <ul className="text-xs text-red-600 space-y-1">
                      {emp.risk_reasons.map((reason, idx) => (
                        <li key={idx}>• {reason}</li>
                      ))}
                    </ul>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={`font-bold ${emp.score < 50 ? 'text-red-600' : 'text-orange-600'}`}>
                      {emp.score}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <Badge className={riskLevelColors[emp.risk_level]}>
                      {emp.risk_level}
                    </Badge>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onViewEmployee && onViewEmployee(emp.employee_id)}
                      className="h-7 text-xs"
                    >
                      Evaluare
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
